import { Application } from 'pixi.js';
import { seekerInput } from '@orbomb/bot';
import { SCENARIOS } from '@orbomb/content';
import { serializeState, type TickInputs } from '@orbomb/sim';
import explosionUrl from './assets/audio/explosion.wav';
import gameReadyUrl from './assets/audio/game-ready.mp3';
import totemStrikeUrl from './assets/audio/totem-strike.mp3';
import { Music } from './audio/music';
import { Sfx } from './audio/sfx';
import { LocalSession } from './game/session';
import { Controls } from './input/controls';
import { colyseusConnect, colyseusReconnect, readColyseusUrl } from './online/colyseus';
import { clearResumeSeat } from './online/resume';
import { attachClientProbe, formatClientProbe, netdiagEnabled } from './debug/netProbe';
import { formatNetStats, LiveMatch } from './online/live';
import { ArenaView } from './render/arena';
import { loadTotemArt } from './render/totemArt';
import {
  loadSettings,
  matchHelp,
  movementCodes,
  planForOnline,
  planForRoom,
  saveSettings,
} from './settings';
import type { Settings } from './settings';
import { HomeScreen } from './ui/home';
import { Hud } from './ui/hud';
import { OnlineUi } from './ui/online';

const TICK_MS = 1000 / 60;

function downloadSnapshot(session: LocalSession): void {
  const blob = new Blob([serializeState(session.state)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `orbomb-tick-${session.state.tick}.json`;
  a.click();
  URL.revokeObjectURL(url);
}

function typingInField(): boolean {
  const el = document.activeElement;
  return el instanceof HTMLInputElement || el instanceof HTMLSelectElement;
}

async function boot(): Promise<void> {
  const canvas = document.getElementById('stage') as HTMLCanvasElement;
  const stick = document.getElementById('stick') as HTMLElement;
  const knob = document.getElementById('stick-knob') as HTMLElement;
  const session = new LocalSession(1);
  if (netdiagEnabled()) session.showDebug = true;
  const controls = new Controls(stick, knob);
  const hud = new Hud(session);
  const sfx = new Sfx(explosionUrl, totemStrikeUrl);
  const music = new Music(gameReadyUrl, () => sfx.bus());
  sfx.onBlast = () => music.duck();
  let settings: Settings = loadSettings();
  controls.setMovementCodes(movementCodes(settings));
  sfx.apply(settings);
  music.apply(settings);
  const home = new HomeScreen(settings);
  for (const el of document.querySelectorAll('[data-app-version]')) {
    el.textContent = `v${__APP_VERSION__}`;
  }
  const help = document.getElementById('help') as HTMLElement;
  let pausedForSettings = false;
  const view = new ArenaView();
  view.setBlastPreview(settings.blastPreview);

  const app = new Application();
  await app.init({
    canvas,
    backgroundAlpha: 0,
    antialias: true,
    autoDensity: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    preference: 'webgl',
  });
  app.ticker.stop();
  await loadTotemArt();
  app.stage.addChild(view.world);
  const probe = attachClientProbe(canvas);

  let acc = 0;
  let live: LiveMatch | null = null;

  const paint = (now = performance.now(), dt = 0) => {
    view.draw(
      live ? live.present(now, dt) : session.state,
      session.config,
      session.showHitboxes,
      now,
    );
    hud.sync(now);
  };

  const resize = () => {
    const wrap = canvas.parentElement;
    if (!wrap) return;
    const w = Math.max(64, wrap.clientWidth);
    const h = Math.max(64, wrap.clientHeight);
    app.renderer.resize(w, h);
    view.layout(w, h, session.config.columns, session.config.rows);
    paint();
  };
  window.addEventListener('resize', resize);
  resize();

  const endOnlineVisit = () => {
    const current = live;
    live = null;
    current?.dispose();
    if (session.onlineCamp) session.endOnline();
    view.setFacing('TURQUOISE');
    view.setLiveBody(null);
    view.clearMotion();
    acc = 0;
  };

  const applyRoomChrome = () => {
    const room = session.roomKind;
    const online = session.onlineCamp !== null;
    stick.hidden =
      !settings.stickEnabled || room === 'computer-computer' || (room === null && !online);
    const dock = document.getElementById('dock');
    const shell = document.getElementById('shell');
    const showStick = !stick.hidden;
    dock?.classList.toggle('has-stick', showStick);
    shell?.classList.toggle('has-stick', showStick);
    shell?.classList.toggle('is-online', online);
    if (!online) help.textContent = room ? matchHelp(settings, room) : '';
    stick.setAttribute(
      'aria-label',
      session.onlineCamp === 'ORANGE' ? 'Orange analogue stick' : 'Turquoise analogue stick',
    );
    controls.setMovementCodes(movementCodes(settings));
    sfx.apply(settings);
    music.apply(settings);
  };

  const readInputs = (): TickInputs => {
    const plan = planForRoom(settings, session.roomKind ?? 'person-computer');
    const raw = controls.read(plan);
    return {
      ORANGE: session.botOrange ? seekerInput(session.state, session.config, 'ORANGE') : raw.ORANGE,
      TURQUOISE: session.botTurquoise
        ? seekerInput(session.state, session.config, 'TURQUOISE')
        : raw.TURQUOISE,
    };
  };

  const startMatch = (seed: number, rematch: boolean) => {
    sfx.stopAll();
    session.reset(seed, rematch);
    view.clearMotion();
    paint();
  };

  const advance = (inputs: TickInputs) => {
    view.anchor(session.state);
    const alreadyOver = Boolean(session.state.result);
    session.advance(inputs);
    view.commit(session.state);
    if (!alreadyOver) sfx.handle(session.lastEvents);
    if (session.state.result && !alreadyOver) session.paused = true;
    paint();
  };

  const leaveMatchScreen = () => {
    session.paused = true;
    pausedForSettings = false;
    sfx.stopAll();
    endOnlineVisit();
  };

  const returnToMenu = () => {
    leaveMatchScreen();
    void online.releaseRoom();
    home.showHome();
  };

  const online = new OnlineUi({
    onStartMatch: (room, camp, lobby, resumeToken = null) => {
      endOnlineVisit();
      try {
        const url = readColyseusUrl();
        live = new LiveMatch(
          session,
          room,
          camp,
          colyseusConnect(url),
          (apply) => {
            apply();
            sfx.handle(session.lastEvents);
          },
          () => {},
          () => returnToMenu(),
          () => lobby.accessToken(),
          resumeToken,
          colyseusReconnect(url),
          (message) => {
            // No seat (match server unreachable, join refused): back to the
            // multiplayer screen with the reason instead of a frozen GET READY.
            leaveMatchScreen();
            void online.abortMatch(message);
          },
        );
      } catch (error) {
        live = null;
        if (session.onlineCamp) session.endOnline();
        throw error;
      }
      view.setFacing(camp);
      view.setLiveBody(camp === 'ORANGE' ? 'player-orange' : 'player-turquoise');
      sfx.resume();
      home.showMatch();
      applyRoomChrome();
      resize();
      paint();
    },
    onLeaveMatch: () => {
      clearResumeSeat();
      returnToMenu();
    },
    onDisposeMatch: () => {
      clearResumeSeat();
      endOnlineVisit();
      // Leave match chrome (stops music) without flashing home — rematch opens lobby next.
      home.showLobby();
    },
    inMatch: () => live !== null,
    // Local matches too: `?demo=watch` starts Watch before the session check ends.
    matchOnScreen: () => live !== null || home.isMatchFocused(),
    showGate: () => home.showGate(),
    showMenu: () => home.showHome(),
    showMulti: () => home.showMulti(),
    showLobby: () => home.showLobby(),
  });

  hud.bind({
    onSeed: (seed) => {
      if (session.onlineCamp) return;
      startMatch(seed, false);
    },
    onStep: () => {
      if (session.onlineCamp) return;
      session.paused = true;
      advance(readInputs());
    },
    onSnapshot: () => downloadSnapshot(session),
    onScenario: (id) => {
      if (session.onlineCamp) return;
      sfx.stopAll();
      session.loadScenario(id);
      view.clearMotion();
      paint();
    },
    onRematch: () => {
      if (!session.onlineCamp) {
        startMatch(session.seed + 1, true);
        return;
      }
      clearResumeSeat();
      void online.requestRematch();
    },
    onDeclineRematch: () => {
      sfx.stopAll();
      session.rematchDeclined = true;
      if (session.onlineCamp) {
        clearResumeSeat();
        returnToMenu();
        return;
      }
      paint();
    },
  });

  home.bind({
    onCreate: (room) => {
      endOnlineVisit();
      void online.releaseRoom();
      sfx.resume();
      session.beginRoom(room, settings);
      view.clearMotion();
      home.showMatch();
      applyRoomChrome();
      resize();
      paint();
    },
    onMulti: () => {
      // No account and no name yet: the sign-in screen, on the name field.
      if (!online.hasPlayer()) {
        online.askForName();
        return;
      }
      home.showMulti();
    },
    onLeave: () => returnToMenu(),
    onChange: (next) => {
      settings = next;
      saveSettings(settings);
      view.setBlastPreview(settings.blastPreview);
      applyRoomChrome();
      resize();
    },
    onTestAudio: () => sfx.preview(),
    onSettingsVisibility: (open) => {
      if (open) {
        online.fillSupabaseFields();
        if (!session.paused) {
          session.paused = true;
          pausedForSettings = true;
        }
        return;
      }
      if (pausedForSettings && home.isMatchFocused()) {
        session.paused = false;
        pausedForSettings = false;
        return;
      }
      if (home.isMatchFocused()) return;
      const authOpen = document.getElementById('auth')?.hidden === false;
      if (online.isSignedIn() && authOpen) home.showHome();
    },
    onMatchView: (on) => music.setInMatch(on),
  });

  if (new URLSearchParams(window.location.search).get('demo') === 'watch') {
    document.getElementById('room-computer-computer')?.click();
  }

  window.addEventListener('keydown', (e) => {
    sfx.resume();
    music.sync();
    if (!home.isMatchFocused() || typingInField()) return;
    if (movementCodes(settings).has(e.code)) return;
    if (e.code === 'KeyM' && !e.repeat) home.toggleSound();
    if (e.code === 'KeyP' && !session.onlineCamp) session.paused = !session.paused;
    if (e.code === 'F1') {
      e.preventDefault();
      session.showDebug = !session.showDebug;
      paint();
    }
    if (e.code === 'F2' && session.showDebug && live) {
      e.preventDefault();
      live.cycleMode();
      paint();
    }
    if (e.code === 'KeyH') {
      session.showHitboxes = !session.showHitboxes;
      paint();
    }
    if (session.onlineCamp) return;
    if (e.code === 'KeyR') {
      startMatch(session.seed, Boolean(session.state.result));
    }
    if (session.state.result && hud.showingResult && (e.code === 'KeyY' || e.code === 'Enter')) {
      document.getElementById('btn-rematch')?.click();
    }
    if (session.state.result && hud.showingResult && e.code === 'KeyN') {
      document.getElementById('btn-no-rematch')?.click();
    }
    if (e.code === 'Period' && session.paused) advance(readInputs());
    const digit = e.code.match(/^Digit([1-6])$/);
    if (digit) {
      const scenario = SCENARIOS[Number(digit[1]) - 1];
      if (scenario) {
        session.loadScenario(scenario.id);
        view.clearMotion();
        paint();
      }
    }
  });

  document.addEventListener('pointerdown', () => {
    sfx.resume();
    music.sync();
  });
  let pausedByHidden = false;
  document.addEventListener('visibilitychange', () => {
    music.setHidden(document.hidden);
    if (live !== null) return;
    if (document.hidden) {
      if (!session.paused) {
        session.paused = true;
        pausedByHidden = true;
      }
      return;
    }
    if (pausedByHidden) {
      session.paused = false;
      pausedByHidden = false;
    }
  });

  online.bind();

  canvas.addEventListener('webglcontextlost', () => {
    console.warn('Orbomb WebGL context lost');
  });
  canvas.addEventListener('webglcontextrestored', () => {
    resize();
    paint();
  });

  let last = performance.now();
  let seenSnapshotTick = -1;
  const frame = (now: number) => {
    requestAnimationFrame(frame);
    try {
      let dt = now - last;
      last = now;
      if (dt > 120) dt = 120;
      if (live) {
        session.waitingForPeer = live.waiting();
        if (!session.paused) acc += dt;
        if (acc > 120) acc = 120;
        if (live.waiting() && acc > TICK_MS * 2) acc = TICK_MS * 2;
        const screen = document.hidden
          ? { x: 0, y: 0 }
          : controls.read(planForOnline(settings)).TURQUOISE;
        live.queue(screen);
        live.flush(now);
        session.waitingForPeer = live.waiting();
        help.textContent = live.statusLine();
        const applied = live.probe().appliedTick;
        if (applied > seenSnapshotTick) {
          probe.noteMessage();
          seenSnapshotTick = applied;
        }
      } else {
        session.waitingForPeer = false;
        session.debugNet = '';
        seenSnapshotTick = -1;
        if (!session.paused) acc += dt;
        while (acc >= TICK_MS) {
          if (!session.paused) advance(readInputs());
          acc -= TICK_MS;
        }
      }
      view.setBlend(
        live
          ? live.drawBlend(now, session.paused)
          : session.paused || acc >= TICK_MS
            ? 1
            : acc / TICK_MS,
      );
      if (live && (session.showDebug || netdiagEnabled())) {
        session.debugNet = [
          formatNetStats(live.netStats(now)),
          formatClientProbe(probe.snapshot()),
        ].join('\n');
      }
      const drawStarted = performance.now();
      paint(now, dt);
      app.render();
      probe.markFrame(dt, performance.now() - drawStarted);
    } catch (error) {
      console.error('Orbomb frame failed', error);
    }
  };
  paint();
  requestAnimationFrame(frame);
}

void boot();
